'use client';

import { X } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import {
  COOKIE_PREFERENCES_OPEN_EVENT,
  hasBrowserPrivacyOptOut,
  readCookieConsent,
  saveCookieConsent,
} from '@/shared/lib/privacy/cookieConsent';
import type { CookieConsent } from '@/shared/lib/privacy/cookieConsent.types';

type ConsentDraft = Pick<CookieConsent, 'analytics' | 'replay'>;
const PUBLIC_CHATBOT_BANNER_BOTTOM_PX = 96;
const COOKIE_CONSENT_GAP_PX = 4;

const isGeneralPublicChatbotPath = (pathname: string | null, locale: string) => {
  if (!pathname) return false;

  const segments = pathname.split('/').filter(Boolean);
  const [firstSegment, secondSegment, thirdSegment] = segments;

  if (firstSegment !== locale || secondSegment !== 'winging' || !thirdSegment) {
    return false;
  }

  return thirdSegment !== 'goheung';
};

const CookieConsentProvider = ({ locale }: { readonly locale: string }) => {
  const isEnglish = locale === 'en';
  const pathname = usePathname();
  const isGeneralPublicChatbot = isGeneralPublicChatbotPath(pathname, locale);
  const isPrivacyOptOutEnabled = hasBrowserPrivacyOptOut();
  const bannerPositionClassName = isGeneralPublicChatbot
    ? 'bottom-[calc(env(safe-area-inset-bottom)+96px)]'
    : 'bottom-[calc(env(safe-area-inset-bottom)+12px)]';
  const [consent, setConsent] = useState<CookieConsent | null | undefined>(
    undefined,
  );
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const [chatAvoidanceBottom, setChatAvoidanceBottom] = useState<
    number | undefined
  >(undefined);
  const [draft, setDraft] = useState<ConsentDraft>({
    analytics: false,
    replay: false,
  });

  useEffect(() => {
    const storedConsent = readCookieConsent();
    let currentConsent = storedConsent;

    if (!storedConsent && !isEnglish) {
      currentConsent = saveCookieConsent({
        essential: true,
        analytics: false,
        replay: false,
      });
    }

    setConsent(currentConsent);
    if (currentConsent) {
      setDraft({
        analytics: currentConsent.analytics,
        replay: currentConsent.replay,
      });
    }

    const openPreferences = () => {
      const currentConsent = readCookieConsent();
      setDraft({
        analytics: currentConsent?.analytics ?? false,
        replay: currentConsent?.replay ?? false,
      });
      setIsPreferencesOpen(true);
    };

    window.addEventListener(COOKIE_PREFERENCES_OPEN_EVENT, openPreferences);
    return () => {
      window.removeEventListener(COOKIE_PREFERENCES_OPEN_EVENT, openPreferences);
    };
  }, [isEnglish]);

  useEffect(() => {
    if (
      !isGeneralPublicChatbot ||
      consent !== null ||
      isPrivacyOptOutEnabled
    ) {
      setChatAvoidanceBottom(undefined);
      return;
    }

    let avoidanceElements: HTMLElement[] = [];

    const measureAvoidanceElements = () => {
      const visibleTops = avoidanceElements
        .map((element) => element.getBoundingClientRect())
        .filter((rect) => rect.width > 0 && rect.height > 0)
        .map((rect) => rect.top);

      if (visibleTops.length === 0) {
        setChatAvoidanceBottom(undefined);
        return;
      }

      const highestAvoidanceTop = Math.min(...visibleTops);
      setChatAvoidanceBottom(
        Math.max(
          PUBLIC_CHATBOT_BANNER_BOTTOM_PX,
          Math.ceil(
            window.innerHeight -
              highestAvoidanceTop +
              COOKIE_CONSENT_GAP_PX,
          ),
        ),
      );
    };

    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(measureAvoidanceElements);

    const refreshAvoidanceElements = () => {
      avoidanceElements = Array.from(
        document.querySelectorAll<HTMLElement>(
          '[data-cookie-consent-avoid]',
        ),
      );
      resizeObserver?.disconnect();
      avoidanceElements.forEach((element) => resizeObserver?.observe(element));
      measureAvoidanceElements();
    };

    const mutationObserver = new MutationObserver(refreshAvoidanceElements);
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
    window.addEventListener('resize', measureAvoidanceElements);
    window.visualViewport?.addEventListener(
      'resize',
      measureAvoidanceElements,
    );
    refreshAvoidanceElements();

    return () => {
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener('resize', measureAvoidanceElements);
      window.visualViewport?.removeEventListener(
        'resize',
        measureAvoidanceElements,
      );
    };
  }, [consent, isGeneralPublicChatbot, isPrivacyOptOutEnabled]);

  const applyConsent = (nextDraft: ConsentDraft) => {
    const nextConsent = saveCookieConsent({
      essential: true,
      ...nextDraft,
    });
    setConsent(nextConsent);
    setIsPreferencesOpen(false);
  };

  const closePreferences = () => {
    if (consent) {
      setIsPreferencesOpen(false);
      return;
    }

    applyConsent({ analytics: false, replay: false });
  };

  if (consent === undefined) return null;

  return (
    <>
      {!consent && !isPrivacyOptOutEnabled && !isPreferencesOpen && (
        <section
          aria-label={isEnglish ? 'Cookie consent' : '쿠키 동의'}
          data-cookie-consent-banner
          data-public-chatbot={isGeneralPublicChatbot || undefined}
          style={
            chatAvoidanceBottom === undefined
              ? undefined
              : { bottom: `${chatAvoidanceBottom}px` }
          }
          className={`fixed right-3 left-3 z-[10000] rounded-[18px] border border-gray-200 bg-white/95 p-4 shadow-[0_18px_54px_rgba(15,23,42,0.18)] backdrop-blur-sm sm:right-4 sm:left-auto sm:w-[420px] ${bannerPositionClassName}`}
        >
          <h2 className="text-label1 font-[700] text-gray-950">
            {isEnglish ? 'Your privacy choices' : '쿠키 사용 선택'}
          </h2>
          <p className="mt-1.5 text-caption1 leading-[1.55] text-gray-600">
            {isEnglish
              ? 'We use optional analytics and session replay only with your consent. Essential cookies keep sign-in and security working.'
              : '선택 동의한 경우에만 분석 및 세션 리플레이를 사용합니다. 필수 쿠키는 로그인과 보안을 위해 사용됩니다.'}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded-[999px] bg-gray-950 px-4 py-2 text-caption1 font-[700] text-white"
              onClick={() =>
                applyConsent({ analytics: true, replay: true })
              }
            >
              {isEnglish ? 'Accept all' : '모두 동의'}
            </button>
            <button
              type="button"
              className="rounded-[999px] border border-gray-200 bg-white px-4 py-2 text-caption1 font-[700] text-gray-700"
              onClick={() =>
                applyConsent({ analytics: false, replay: false })
              }
            >
              {isEnglish ? 'Essential only' : '필수만 사용'}
            </button>
            <button
              type="button"
              className="rounded-[999px] px-3 py-2 text-caption1 font-[700] text-gray-500 hover:bg-gray-50"
              onClick={() => setIsPreferencesOpen(true)}
            >
              {isEnglish ? 'Settings' : '설정'}
            </button>
          </div>
        </section>
      )}

      {isPreferencesOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-preferences-title"
          className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/25 p-3 sm:items-center sm:p-4"
        >
          <section className="w-full max-w-[440px] rounded-[20px] bg-white p-5 shadow-[0_24px_72px_rgba(15,23,42,0.22)]">
            <div className="flex items-start justify-between gap-4">
              <h2
                id="cookie-preferences-title"
                className="text-body2 font-[700] text-gray-950"
              >
                {isEnglish ? 'Cookie Preferences' : '쿠키 설정'}
              </h2>
              <button
                type="button"
                aria-label={isEnglish ? 'Close cookie settings' : '쿠키 설정 닫기'}
                className="flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                onClick={closePreferences}
              >
                <X className="h-[16px] w-[16px]" aria-hidden="true" />
              </button>
            </div>
            {isPrivacyOptOutEnabled && (
              <p className="mt-3 rounded-[12px] bg-gray-50 p-3 text-caption1 leading-[1.55] text-gray-600">
                {isEnglish
                  ? 'Your browser privacy signal is enabled, so optional analytics and session replay stay off.'
                  : '브라우저 개인정보 보호 신호가 켜져 있어 선택 분석과 세션 리플레이를 사용하지 않습니다.'}
              </p>
            )}
            <div className="mt-4 flex flex-col gap-3">
              <label className="flex items-center justify-between gap-4 rounded-[14px] bg-gray-50 px-4 py-3">
                <span>
                  <strong className="text-label1 text-gray-900">
                    {isEnglish ? 'Essential' : '필수'}
                  </strong>
                  <span className="mt-1 block text-caption1 text-gray-500">
                    {isEnglish
                      ? 'Required for sign-in and security.'
                      : '로그인과 보안에 필요합니다.'}
                  </span>
                </span>
                <input type="checkbox" checked disabled />
              </label>
              <label className="flex items-center justify-between gap-4 rounded-[14px] border border-gray-100 px-4 py-3">
                <span>
                  <strong className="text-label1 text-gray-900">
                    {isEnglish ? 'Analytics' : '분석'}
                  </strong>
                  <span className="mt-1 block text-caption1 text-gray-500">
                    {isEnglish
                      ? 'Google Analytics and Microsoft Clarity.'
                      : 'Google Analytics와 Microsoft Clarity입니다.'}
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={!isPrivacyOptOutEnabled && draft.analytics}
                  disabled={isPrivacyOptOutEnabled}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      analytics: event.target.checked,
                    }))
                  }
                />
              </label>
              <label className="flex items-center justify-between gap-4 rounded-[14px] border border-gray-100 px-4 py-3">
                <span>
                  <strong className="text-label1 text-gray-900">
                    {isEnglish ? 'Session replay' : '세션 리플레이'}
                  </strong>
                  <span className="mt-1 block text-caption1 text-gray-500">
                    {isEnglish
                      ? 'Helps us diagnose errors in Sentry.'
                      : 'Sentry에서 오류를 진단하는 데 사용됩니다.'}
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={!isPrivacyOptOutEnabled && draft.replay}
                  disabled={isPrivacyOptOutEnabled}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      replay: event.target.checked,
                    }))
                  }
                />
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              {consent && (
                <button
                  type="button"
                  className="rounded-[999px] border border-gray-200 px-4 py-2 text-caption1 font-[700] text-gray-700"
                  onClick={() => setIsPreferencesOpen(false)}
                >
                  {isEnglish ? 'Cancel' : '취소'}
                </button>
              )}
              <button
                type="button"
                className="rounded-[999px] bg-primary px-5 py-2 text-caption1 font-[700] text-white"
                onClick={() => applyConsent(draft)}
              >
                {isEnglish ? 'Save choices' : '선택 저장'}
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default CookieConsentProvider;
